// ============================================================================
// REQ-2026-0007 — Triaxial Analysis range-typed picker (Max / Peak / End / Residual)
//
// Mirrors the Direct Shear PeakResidualInputs pattern (frontend/src/pages/
// TestWorkflow/analysis-plots/PeakResidualInputs.tsx) — from/to TextField pair
// per slot, save button, accessibility-coloured slot markers.
//
// Slot colours follow the spec accessibility palette (Q7.8):
//   Max      #2f5d8a (blue)
//   Peak     #2e8b57 (green)
//   End      #c7860b (orange)
//   Residual #c0392b (red)
//
// Slot naming is reversible per Gigi (Q7.2).
// ============================================================================

import { Box, Button, CircularProgress, Paper, TextField, Typography, Tooltip } from '@mui/material';
import { Save } from '@mui/icons-material';
import { TRIAXIAL_SLOT_COLORS, type TriaxialSlot } from './triaxialPickerColors';
import type { SlotRange, SlotSnapshot } from './triaxialPickerResolver';

interface SlotRow {
  slot: TriaxialSlot;
  label: string;
  range: SlotRange;
  snapshot: SlotSnapshot;
}

interface Props {
  rows: SlotRow[];
  saving: boolean;
  readOnly?: boolean;
  onRangeChange: (slot: TriaxialSlot, key: 'fromPct' | 'toPct', value: string) => void;
  onSave: () => void;
}

const fmt = (v: number | null | undefined, d: number): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
};

const numStr = (v: number | null): string => (v === null ? '' : String(v));

const SLOT_TIPS: Record<TriaxialSlot, string> = {
  max: "Max — row with max deviator stress q within [from, to]",
  peak: "Peak — row with max effective stress ratio σ'₁/σ'₃ within [from, to]",
  end: "End — row with max axial strain εₐ within [from, to] (= last point in window)",
  residual: 'Residual — row with min deviator stress q within [from, to] (post-peak residual strength)',
};

export const TriaxialPickerInputs = ({
  rows, saving, readOnly, onRangeChange, onSave,
}: Props) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Analysis Picker — type εₐ ranges (from / to) per slot; values resolve from the data
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
        {rows.map((r) => (
          <SlotRowView
            key={r.slot}
            row={r}
            readOnly={readOnly}
            onRangeChange={onRangeChange}
          />
        ))}
      </Box>
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 14 }} />}
          onClick={onSave} disabled={readOnly || saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save Picker'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Each slot's derived values (q, p, p′, t, s, σ′₁/σ′₃, Δu) update live from the resolved row.
        </Typography>
      </Box>
    </Paper>
  );
};

interface SlotRowViewProps {
  row: SlotRow;
  readOnly?: boolean;
  onRangeChange: (slot: TriaxialSlot, key: 'fromPct' | 'toPct', value: string) => void;
}

function SlotRowView({ row, readOnly, onRangeChange }: SlotRowViewProps) {
  const color = TRIAXIAL_SLOT_COLORS[row.slot];
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      px: 1, py: 0.5,
      border: '1px solid', borderColor: 'divider', borderRadius: 1,
      backgroundColor: 'background.paper',
    }}>
      <Tooltip title={SLOT_TIPS[row.slot]}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          minWidth: 86,
        }}>
          <Box sx={{
            width: 12, height: 12, borderRadius: '50%',
            backgroundColor: color, flex: '0 0 auto',
            border: '1px solid', borderColor: 'divider',
          }} aria-label={`${row.label} colour marker`} />
          <Typography variant="caption" sx={{ fontWeight: 700, color }}>
            {row.label}
          </Typography>
        </Box>
      </Tooltip>
      <TextField
        size="small" type="number" label="From εₐ %"
        sx={{ width: 96 }}
        InputProps={{ sx: { fontSize: '0.78rem', py: 0 } }}
        InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
        inputProps={{ step: 0.01 }}
        value={numStr(row.range.fromPct)}
        onChange={(e) => onRangeChange(row.slot, 'fromPct', e.target.value)}
        disabled={readOnly}
      />
      <TextField
        size="small" type="number" label="To εₐ %"
        sx={{ width: 96 }}
        InputProps={{ sx: { fontSize: '0.78rem', py: 0 } }}
        InputLabelProps={{ sx: { fontSize: '0.7rem' } }}
        inputProps={{ step: 0.01 }}
        value={numStr(row.range.toPct)}
        onChange={(e) => onRangeChange(row.slot, 'toPct', e.target.value)}
        disabled={readOnly}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 0.5, flex: 1, minWidth: 0 }}>
        <DerivedChip label="εₐ" value={fmt(row.snapshot.axialStrainPct, 2)} unit="%" />
        <DerivedChip label="q" value={fmt(row.snapshot.deviatorStressKPa, 0)} unit="kPa" />
        <DerivedChip label="σ′₁/σ′₃" value={fmt(row.snapshot.effStressRatio, 2)} unit="" />
        <DerivedChip label="Δu" value={fmt(row.snapshot.excessPwpKPa, 0)} unit="kPa" />
      </Box>
    </Box>
  );
}

interface DerivedChipProps {
  label: string;
  value: string;
  unit: string;
}

function DerivedChip({ label, value, unit }: DerivedChipProps) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
      px: 0.75, py: 0.15,
      backgroundColor: 'action.hover', borderRadius: 0.75,
      fontSize: '0.7rem', whiteSpace: 'nowrap',
    }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>{value}</Typography>
      {unit && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>{unit}</Typography>}
    </Box>
  );
}
